import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip, Alert } from "@mui/material";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, SelectField, DataTable, FormDialog } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { profile, social, canEdit, titleHas, date, PlayerLink, ConfirmAction, COSMETIC_KINDS } from "./community";

// Profiles → Player (service `profile`, Docs/Social.md §14.8 / §14.9): one account's profile - `profile get <player>` (the selection,
// owned per kind with the unseen ids, private, the versions, its View as the owner sees it) - and the operator's verbs: `profile grant`
// / `revoke <player> <cosmeticId>` (a picker from `profile catalogue`), `profile set <player> <picture> <border> <badge> <title>`
// ("-" = none; a picture "-" keeps the current one), private on / off (the profile group has no verb for it: Evil Tower's `social
// private <player> on|off` writes both flags in one update, shown where the title has `social` and the account its role).
// `#profile` views, `profile` acts. /<game>/profiles/player/<link|id> opens a player.
const NONE = "-";

export default class ProfilesPlayerTab extends React.Component {
    state = { player: "", data: null, error: null, defs: {}, defaultPicture: "", busy: false, action: null, notice: null, grantId: "", selecting: null };

    componentDidMount() {
        this.loadCatalogue();
        const pending = globals.route?.deep?.[0];
        if (pending) this.setState({ player: pending }, this.lookup);
    }

    // The catalogue: the grant picker and every id's kind / art / name tag / enabled
    loadCatalogue = () => {
        profile("Loading the catalogue", ["catalogue"], { silent: true })
            .then(r => {
                if (!r || typeof r !== "object") return;
                const defs = {};
                for (const c of r.cosmetics ?? []) defs[c.id] = c;
                this.setState({ defs, defaultPicture: r.defaultPicture ?? "" });
            })
            .catch(() => {});
    }

    lookup = () => {
        const p = this.state.player.trim();
        if (!p) return;
        this.setState({ error: null, notice: null });
        profile("Loading the profile", ["get", p], { silent: true })
            .then(r => {
                if (r && typeof r === "object") { this.setState({ data: r }); replaceDeep([p]); }
                else this.setState({ data: null, error: String(r ?? "No answer.") });
            })
            .catch(e => this.setState({ data: null, error: e?.message ?? "failed" }));
    }

    // One operator verb; `apply(answer)` turns its answer into the new state (a notice, the profile)
    act = (group, parts, description, apply) => {
        this.setState({ busy: true });
        return group(description, parts, { successMessage: "Answered" })
            .then(r => { this.setState({ action: null, selecting: null }); apply(r); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    // grant / revoke answer { outcome, cosmetic, profile }: Granted / Revoked are the landed ones, the rest say why nothing moved
    grantOrRevoke = (sub, id) => {
        const d = this.state.data;
        const def = this.state.defs[id];
        this.setState({ action: {
            title: `${sub === "grant" ? "Grant" : "Revoke"} ${id} ${sub === "grant" ? "to" : "from"} ${d.view?.name || d.playerId}?`,
            confirmText: sub === "grant" ? "Grant" : "Revoke",
            text: sub === "grant"
                ? `${def ? `${def.kind}, rarity ${def.rarity}${def.enabled ? "" : ", DISABLED (owned, but Set refuses it)"}. ` : ""}It lands in the owned list and the picker shows it as new; a repeat answers AlreadyOwned (source admin:<you>).`
                : "Out of owned and unseen; a selected one falls back to none (the picture: the default).",
            run: () => this.act(profile, [sub, d.playerId, id], sub === "grant" ? "Granting" : "Revoking", r => {
                const landed = r?.outcome === "Granted" || r?.outcome === "Revoked";
                this.setState({ notice: { severity: landed ? "success" : "warning", text: `${sub} ${r?.cosmetic ?? id}: ${r?.outcome ?? "?"}` }, grantId: "" });
                if (r?.profile && typeof r.profile === "object") this.setState({ data: r.profile }); else this.lookup();
            }),
        }});
    }

    // set answers the profile as it is now, or { refused, message } (40 not in the catalogue / disabled / another kind, 41 not owned)
    saveSelection = () => {
        const d = this.state.data, s = this.state.selecting;
        this.act(profile, ["set", d.playerId, s.picture || NONE, s.border || NONE, s.badge || NONE, s.title || NONE], "Setting the selection", r => {
            if (r?.refused !== undefined) { this.setState({ notice: { severity: "error", text: `Set refused (${r.refused}): ${r.message}` } }); return; }
            this.setState({ notice: { severity: "success", text: "Selection set." } });
            if (r && typeof r === "object" && r.playerId) this.setState({ data: r }); else this.lookup();
        });
    }

    setPrivate = (on) => {
        const d = this.state.data;
        this.setState({ action: {
            title: `Private ${on ? "on" : "off"} for ${d.view?.name || d.playerId}?`, confirmText: on ? "Make private" : "Make public",
            text: on ? "Others see the header only (name, picture, border, badge, title); the title's public lists drop the player (Evil Tower: the feed's next rebuild)."
                     : "The profile is public again: its stats and online state show to others.",
            run: () => this.act(social, ["private", d.playerId, on ? "on" : "off"], "Switching private", r => {
                this.setState({ notice: { severity: "success", text: r?.done ?? `Private ${on ? "on" : "off"}.` } });
                this.lookup();
            }),
        }});
    }

    // One cosmetic id as the catalogue knows it: the id, a "not in the catalogue" / "disabled" flag
    cosmetic = (id, { unseen, onDelete, isDefault } = {}) => {
        const def = this.state.defs[id];
        const flags = [isDefault ? "default" : null, unseen ? "new" : null, def && !def.enabled ? "disabled" : null, !def && Object.keys(this.state.defs).length ? "not in the catalogue" : null].filter(Boolean);
        return (
            <Chip key={id} size="small" label={flags.length ? `${id} · ${flags.join(", ")}` : id} onDelete={onDelete}
                  color={unseen ? "info" : def && !def.enabled ? "warning" : !def && Object.keys(this.state.defs).length ? "error" : "default"}
                  variant={unseen ? "filled" : "outlined"} sx={{ mr: 0.5, mb: 0.5 }}
                  title={def ? `${def.kind} · art ${def.art || "(none)"} · ${def.name} · rarity ${def.rarity} · source ${def.source || "—"}` : id} />
        );
    }

    renderSelectionDialog() {
        const { data, selecting, defs, defaultPicture, busy } = this.state;
        if (!data || !selecting) return null;
        const owned = data.owned ?? {};
        const optionsOf = (kind) => {
            const ids = [...(kind === "picture" && defaultPicture ? [defaultPicture] : []), ...(owned[kind] ?? [])];
            const current = selecting[kind];
            if (current && current !== NONE && !ids.includes(current)) ids.push(current);
            const opts = [...new Set(ids)].map(id => ({ value: id, label: `${id}${defs[id] && !defs[id].enabled ? " (disabled: refused)" : ""}${id === defaultPicture && kind === "picture" ? " (default)" : ""}` }));
            return [{ value: NONE, label: kind === "picture" ? "(keep the current one)" : "(none)" }, ...opts];
        };
        return (
            <FormDialog open title={`Selection of ${data.view?.name || data.playerId}`} onClose={() => this.setState({ selecting: null })} maxWidth="sm" fullWidth
                        confirmText="Set" confirmDisabled={busy} onConfirm={this.saveSelection}>
                <Typography variant="body2" sx={{ mb: 2 }}>Set's rules: every id owned (or the default picture), enabled and of its kind; the whole selection in one write.</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {COSMETIC_KINDS.map(kind => (
                        <SelectField key={kind} label={kind} size="small" fullWidth value={selecting[kind] || NONE} options={optionsOf(kind)}
                                     onChange={v => this.setState(s => ({ selecting: { ...s.selecting, [kind]: v } }))} />
                    ))}
                </Box>
            </FormDialog>
        );
    }

    render() {
        const { player, data, error, defs, defaultPicture, busy, action, notice, grantId } = this.state;
        const edit = canEdit("profile");
        const privateVerb = titleHas("social") && canEdit("social");
        const sel = data?.selection ?? {};
        const owned = data?.owned ?? {};
        const unseen = new Set(data?.unseen ?? []);
        const selectionRows = COSMETIC_KINDS.map(kind => ({ kind, id: sel[kind] ?? "" }));
        const selectionColumns = [
            { id: "kind", label: "Slot", width: "12%" },
            { id: "id", label: "Selected", width: "26%", render: r => r.id ? <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{r.id}</span> : <Typography variant="body2" color="text.secondary">none</Typography> },
            { id: "art", label: "Art", width: "22%", render: r => <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{defs[r.id]?.art ?? ""}</span> },
            { id: "name", label: "Name tag", width: "26%", render: r => <span style={{ wordBreak: "break-all" }}>{defs[r.id]?.name ?? ""}</span> },
            { id: "note", label: "", width: "14%", render: r => {
                if (!r.id) return "";
                if (r.kind === "picture" && !sel.pictureStored) return <Chip size="small" label="default" />;
                if (defs[r.id] && !defs[r.id].enabled) return <Chip size="small" color="warning" label="disabled" />;
                if (r.kind !== "picture" || r.id !== defaultPicture) { if (!(owned[r.kind] ?? []).includes(r.id)) return <Chip size="small" color="error" label="not owned" />; }
                return "";
            } },
        ];
        const ownedColumns = [
            { id: "kind", label: "Kind", width: "12%" },
            { id: "count", label: "Owned", width: "8%", align: "right", render: r => (owned[r.kind] ?? []).length },
            { id: "ids", label: `Cosmetics (blue = unseen: the picker shows a "new" dot${edit ? "; × revokes" : ""})`, width: "80%", render: r => (
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    {r.kind === "picture" && defaultPicture && this.cosmetic(defaultPicture, { isDefault: true })}
                    {(owned[r.kind] ?? []).map(id => this.cosmetic(id, { unseen: unseen.has(id), onDelete: edit ? () => this.grantOrRevoke("revoke", id) : undefined }))}
                    {(owned[r.kind] ?? []).length === 0 && !(r.kind === "picture" && defaultPicture) && <Typography variant="body2" color="text.secondary">none</Typography>}
                </Box>) },
        ];
        const ownedAll = new Set(COSMETIC_KINDS.flatMap(k => owned[k] ?? []));
        const grantOptions = Object.values(defs)
            .filter(c => !ownedAll.has(c.id) && !(c.kind === "picture" && c.id === defaultPicture))
            .map(c => ({ value: c.id, label: `${c.kind} · ${c.id}${c.enabled ? "" : " (disabled)"}` }));
        return (
            <PageContainer>
                <SectionHeader title="Profile">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · a player's header, owned cosmetics and privacy</Typography>
                </SectionHeader>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField label="Player (link or id)" size="small" value={player} onChange={e => this.setState({ player: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.lookup(); }} sx={{ width: 300 }} />
                        <Button variant="outlined" onClick={this.lookup} disabled={busy || !player.trim()}>Look up</Button>
                        {data && (
                            <Typography variant="body2" component="div">
                                <b>{data.view?.name ?? "(no view)"}</b> · <PlayerLink id={data.playerId} /> · {data.branch}
                                {" "}<Chip size="small" color={data.isPrivate ? "warning" : "success"} label={data.isPrivate ? "private" : "public"} sx={{ ml: 0.5 }} />
                                {data.view?.online ? <Chip size="small" color="success" variant="outlined" label="online" sx={{ ml: 0.5 }} /> : null}
                            </Typography>
                        )}
                    </Box>
                    {data && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {data.hasProfile ? `profile written ${date(data.at)}` : "no profile document yet (the default picture, nothing owned)"} · versions.cosmetics {data.versionsCosmetics} (domain 201 v{data.domainVersion}) · {unseen.size} unseen
                            {data.viewStatus ? ` · View answers ${data.viewStatus}${data.viewMessage ? ` (${data.viewMessage})` : ""}` : ""}
                        </Typography>
                    )}
                    {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
                    {notice && <Alert severity={notice.severity} sx={{ mt: 1 }} onClose={() => this.setState({ notice: null })}>{notice.text}</Alert>}
                </Paper>
                {data && (
                    <>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, gap: 1, flexWrap: "wrap" }}>
                                <Typography variant="subtitle2">Header</Typography>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                    {edit && <Button size="small" variant="outlined" disabled={busy}
                                                     onClick={() => this.setState({ selecting: { picture: sel.picture || NONE, border: sel.border || NONE, badge: sel.badge || NONE, title: sel.title || NONE } })}>Set selection</Button>}
                                    {privateVerb && <Button size="small" variant="outlined" color={data.isPrivate ? "primary" : "warning"} disabled={busy} onClick={() => this.setPrivate(!data.isPrivate)}>
                                        {data.isPrivate ? "Private off" : "Private on"}</Button>}
                                </Box>
                            </Box>
                            <DataTable sx={FIXED_TABLE} columns={selectionColumns} rows={selectionRows} getRowKey={r => r.kind} />
                            {edit && !privateVerb && <Typography variant="caption" color="text.secondary">Private on / off: no admin verb here (the profile group has none; Evil Tower's `social private` needs the social role).</Typography>}
                        </Paper>
                        <Paper sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Owned</Typography>
                            <DataTable sx={FIXED_TABLE} columns={ownedColumns} rows={COSMETIC_KINDS.map(kind => ({ kind }))} getRowKey={r => r.kind} />
                            {edit && (
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mt: 2 }}>
                                    <SelectField label="Cosmetic to grant" size="small" minWidth={340} value={grantId} options={grantOptions} onChange={v => this.setState({ grantId: v })} />
                                    <Button variant="contained" disabled={busy || !grantId} onClick={() => this.grantOrRevoke("grant", grantId)}>Grant</Button>
                                    {grantOptions.length === 0 && <Typography variant="body2" color="text.secondary">{Object.keys(defs).length ? "Owns every cosmetic of the catalogue." : "The catalogue is empty (Cosmetics → Import)."}</Typography>}
                                </Box>
                            )}
                        </Paper>
                    </>
                )}
                {this.renderSelectionDialog()}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
